/**
 * @typedef {{ [key: string]: any }} Collection
 * @typedef {{ [key: string]: any }} Bundle
 * @typedef {{ [key: string]: any, options: string[] }} Variant
 * @typedef {{ [key: string]: any, values: string[] }} Option
 * @typedef {{ [key: string]: any, values: Array<{ [key: string]: any }> }} CustomOption
 * @typedef {{ [key: string]: any, variants: Variant[], selected: Variant | null, custom_options: CustomOption[], options: Option[]}} Product
 * @typedef {{ [key: string]: any, nlTranslations: { [key: string]: string } }} NalaSettings
 * @typedef {{ [key: string]: any }} NalaDistributor
 * @typedef {{ [key: string]: any, phones: string[], emails: string[], websites: string[], socials: { [key: string]: string }[] }} Distributor
 * @typedef {{ distributors: { [key: string]: Distributor[] } }} DistributorResponse
 * @typedef {{ [key: string]: any, LatLng: { [key: string]: any }[] }} MarkerInfo
 * @typedef {{ [key: string]: any }} BAATierPrice
 * @typedef {{ [key: string]: any }} NalaWholesaleCustomerData
 * @typedef {{ [key: string]: any }} CustomerFormData
 * @typedef {{ ready: (callback: () => void) => void, execute: (siteKey: string, options: { action: string }) => Promise<string> }} Grecaptcha
 * @typedef {{ [key: string]: any }} CartItem
 * @typedef {{ [key: string]: any, items: Array<CartItem> }} NalaCart
 */

import { CartAddEvent, FilterUpdateEvent, ThemeEvents } from "@theme/events";

/**
 * Nala Base Class
 * @extends {HTMLElement}
 */
export class NalaBase extends HTMLElement {
  constructor() {
    super();

    // @ts-ignore - NalaSettings is a global object initialized in other files
    this.settings = /** @type {NalaSettings} */ (window.NalaSettings);
    this.rate = 1;
    this.cart = /** @type {NalaCart|null} */ (null);
    this.products = /** @type {{[key: string]: Product}} */ ({});
  }

  /**
   * Set a cookie
   * @param {string} name - The name of the cookie
   * @param {string} value - The value of the cookie
   * @param {number} days - The number of days to store the cookie
   */
  nlSetCookie(name, value, days = 365) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
  }

  /**
   * Get a cookie
   * @param {string} name - The name of the cookie
   * @param {string} defaultValue - The default value to return if the cookie is not found
   * @returns {string} The value of the cookie
   */
  nlGetCookie(name, defaultValue = "") {
    const cname = `${name}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let c of ca) {
      while (c.charAt(0) === " ") c = c.substring(1);
      if (c.indexOf(cname) === 0) {
        return c.substring(cname.length, c.length);
      }
    }
    return defaultValue;
  }

  /**
   * Delete a cookie
   * @param {string} name - The name of the cookie
   */
  nlDeleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  /**
   * Format a money amount according to the specified format
   * @param {string|number} cents - Amount in cents
   * @param {string|null} format - Money format string
   * @param {boolean} convert - Whether to convert the currency
   * @returns {string} Formatted money string
   */
  nlFormatMoney(cents, format = null, convert = true) {
    // @ts-ignore - Accessing Shopify global object
    this.rate = convert ? parseFloat(window.Shopify?.currency?.rate || 1) : 1;
    if (typeof cents == "string") cents = cents.replace(".", "");
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || this.settings.nlMoneyFormat || "${{amount}}";

    let value = "";
    // @ts-ignore - match is a method of String
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = this.nlFormatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = this.nlFormatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = this.nlFormatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = this.nlFormatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /**
   * @param {string|number} cents - Amount in cents
   * @param {number} precision - Decimal precision
   * @param {string} thousands - Thousands separator
   * @param {string} decimal - Decimal separator
   * @returns {string} Formatted number
   */
  nlFormatWithDelimiters(cents, precision = 2, thousands = ",", decimal = ".") {
    const number = Number(cents);
    if (isNaN(number) || cents == null) {
      return "0";
    }
    const formattedNumber = ((number * this.rate) / 100.0).toFixed(precision);
    const parts = formattedNumber.split(".");
    // @ts-ignore - parts is an array
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands
    );
    const dcents = parts[1] ? decimal + parts[1] : "";

    return dollars + dcents;
  }

  /**
   * @param {string} str
   * @param {string} reptext
   * @returns
   */
  nlHandleize(str, reptext = "-") {
    return str?.toLowerCase()?.replace(/[^\w\u00C0-\u024f]+/g, reptext);
  }

  /**
   * @description Update the cart sections
   * @param {string} id - The variant ID
   * @param {number} qty - The quantity of the variant
   * @param {string} productId - The product ID
   */
  async nlUpdateCartSections(id, qty, productId) {
    if (this.settings.nlPageTemplate == "cart") {
      window.location.href = Theme.routes.cart_url;
    } else {
      this.dispatchEvent(
        new CartAddEvent({}, id, {
          source: "product-form-component",
          itemCount: Number(qty),
          productId: productId,
        })
      );
    }
  }

  /**
   * @description Get the current cart
   * @param {boolean} reload
   * @returns {Promise<NalaCart|null>} The current cart object
   */
  async nlGetCurrentCart(reload = false) {
    if (this.cart == null || reload == true) {
      try {
        // @ts-ignore - Accessing Shopify global object
        const root = window.Shopify?.routes?.root || "/";
        // Fetch the cart data from the Shopify API
        const response = await fetch(root + "cart.js");
        if (!response.ok) {
          throw new Error("Failed to fetch cart data");
        }
        this.cart = await response.json();
      } catch (error) {
        console.error("Error fetching cart data:", error);
        this.cart = null;
      }
    }
    return this.cart;
  }

  /**
   * @description get the product data
   * @param {string} product_id
   * @param {string} url
   * @returns {Promise<Product>}
   */
  async nlGetProduct(product_id, url) {
    if (this.products.hasOwnProperty(product_id)) {
      return /** @type {Product} */ (this.products[product_id]);
    }
    if (url.includes("?")) {
      url = `${url}&view=nala`;
    } else {
      url = `${url}?view=nala`;
    }
    const response = await fetch(url);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const element = doc.getElementById("nalaProduct");

    let product = /** @type {Product} */ ({});
    if (element) {
      const jsonProduct = JSON.parse(element.innerText);
      product = this.nlFormatProductData(jsonProduct);
    }
    this.products[product_id] = product;
    return product;
  }

  /**
   * @description format the product data
   * @param {Product} product
   * @returns {Product}
   */
  nlFormatProductData(product) {
    return {
      ...product,
      category: product.category || "",
      vendor: product.vendor || "",
      type: product.type || "",
      is_default_variant: product.variants.length === 1,
      variants: product.variants.map((variant) => {
        return {
          ...variant,
          sku: variant.sku || "",
          qty: 0,
          loading: false,
        };
      }),
    };
  }

  /**
   * @param {string} url
   * @param {string} type
   */
  nlOpenLink(url, type = "_self") {
    if (type == "_self") {
      window.location.href = url;
    } else {
      window.open(url, type);
    }
  }

  /**
   * @param {string} productId
   * @param {CartItem[]} items
   * @param {boolean} openCart
   * @param {string} eventName
   * @returns {Promise<boolean>}
   */
  async nlAddToCart(productId, items, openCart = true, eventName = "nala-cart-updated") {
    try {
      // @ts-ignore - Accessing Shopify global object
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        throw new Error("Failed to add to cart");
      }
      const cartData = await response.json();

      const item = items[0];
      if (openCart && item) {
        await this.nlUpdateCartSections(item.id, item.quantity, productId);
      }
      if (eventName) {
        this.dispatchEvent(new CustomEvent(eventName, {
          detail: {
            cart: cartData,
          }
        }));
      }
      return true;
    } catch (error) {
      console.error("Error add to cart", error);
      return false;
    }
  }

  /**
   * Clears the cart
   * @param {string} eventName
   * @returns {Promise<void>}
   */
  async nlClearCart(eventName = "nala-cart-updated") {
    // @ts-ignore - Accessing Shopify global object
    const root = window.Shopify?.routes?.root || "/";
    fetch(root + "cart/clear.js", {
      method: "POST",
    });
    if (eventName) {
      this.dispatchEvent(new CustomEvent(eventName, {
        detail: {
          cart: {},
        }
      }));
    }
  }

  /**
   * @param {string} type
   * @param {number} size
   * @returns {string}
   */
  nlGetIcon(
    type,
    size = 24,
    strokeWidth = 2,
    fill = "none",
    stroke = "currentColor"
  ) {
    return `
    <span class="nala-icon-wrapper">
      <svg
        class="nala-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 ${size} ${size}"
        fill="${fill}" 
        stroke="${stroke}"
        stroke-width="${strokeWidth}"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        ${this.nlGetIconBody(type, fill, stroke, strokeWidth)}
      </svg>
    </span>`;
  }

  /**
   * @param {string} type
   * @returns {string}
   */
  nlGetIconBody(type, fill = "none", stroke = "currentColor", strokeWidth = 2) {
    switch (type) {
      case "address":
        return `
          <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" />
          <path d="M12 22C8 18 4 13.3137 4 10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10C20 13.3137 16 18 12 22Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "phone":
        return `
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "email":
        return `
          <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "website":
        return `
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2 12H22" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "facebook":
        return `
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "instagram":
        return `
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "twitter":
        return `
          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;
      case "tiktok":
        return `
          <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;

      case "tag":
        return `
          <path d="M57.993,2.97c-0.051-0.748-0.646-1.343-1.394-1.394L33.658,0.004C33.224-0.029,32.8,0.134,32.495,0.44L0.439,32.495  C0.158,32.776,0,33.158,0,33.556s0.158,0.779,0.439,1.061l24.513,24.512c0.293,0.293,0.677,0.439,1.061,0.439  s0.768-0.146,1.061-0.439l32.054-32.054c0.307-0.306,0.466-0.73,0.436-1.163L57.993,2.97z M26.013,55.946L3.622,33.556L34.133,3.044  l20.956,1.436l1.435,20.956L26.013,55.946z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" /><path d="M41.568,10.721c-1.944,0-3.771,0.757-5.146,2.132C35.047,14.228,34.29,16.056,34.29,18s0.758,3.771,2.133,5.146  c1.375,1.375,3.202,2.132,5.146,2.132c1.943,0,3.771-0.756,5.146-2.131c2.837-2.838,2.836-7.455-0.002-10.293  C45.339,11.479,43.512,10.721,41.568,10.721z M44.594,21.025c-0.808,0.808-1.883,1.252-3.025,1.252s-2.217-0.445-3.025-1.253  C37.735,20.216,37.29,19.142,37.29,18s0.445-2.216,1.254-3.025c0.808-0.808,1.882-1.253,3.024-1.253s2.217,0.445,3.024,1.253  C46.26,16.643,46.261,19.357,44.594,21.025z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
        `;

      case "chevron-down":
        return `
          <path d="M8.542 2.558a.625.625 0 0 1 0 .884l-3.6 3.6a.626.626 0 0 1-.884 0l-3.6-3.6a.625.625 0 1 1 .884-.884L4.5 5.716l3.158-3.158a.625.625 0 0 1 .884 0z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
        `;

      default:
        return "";
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} selector
   * @param {string} value
   */
  nlSetFieldValue(form, selector, value) {
    /** @type {HTMLInputElement|HTMLSelectElement|null} */
    const field = form.querySelector(selector);
    if (field) field.value = value || "";
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} selector
   * @param {boolean} checked
   */
  nlSetCheckboxChecked(form, selector, checked) {
    /** @type {HTMLInputElement|null} */
    const field = form.querySelector(selector);
    if (field) field.checked = checked;
  }

  /**
   * Validates a required form field
   * Adds error styling to input, label, and displays error message if field is empty
   * @param {HTMLFormElement} form - The form element to validate
   * @param {string} className - The CSS class name prefix for the field (e.g., "first_name" for .first_name_input, .first_name_label, .first_name_error)
   * @returns {void}
   */
  nlRequiredField(form, className) {
    if (!(form instanceof HTMLFormElement)) return;

    // get input element
    /** @type {HTMLInputElement|null} */
    const input = form.querySelector(`.${className}_input`);
    if (!input) return;

    // get label and error elements
    const label = form.querySelector(`.${className}_label`);
    const error = form.querySelector(`.${className}_error`);

    // check if value is not empty
    const value = input.value.trim();
    if (value !== "") {
      // clear error
      input.classList.remove("error");
      if (label) label.classList.remove("error");
      if (error) error.textContent = "";
      return;
    }

    // set error and add error class to input, label, and error element
    this.error = true;
    input.classList.add("error");
    if (label) label.classList.add("error");
    if (error) error.textContent = "Required Field";
  }

  /**
   * Validates that password and confirm password fields match
   * Adds error styling to both password fields and displays error message if they don't match
   * @param {HTMLFormElement} form - The form element to validate
   * @param {string} passwordClassName - The CSS class name prefix for the password field
   * @param {string} confirmPasswordClassName - The CSS class name prefix for the confirm password field
   * @returns {void}
   */
  nlPasswordMatch(form, passwordClassName, confirmPasswordClassName) {
    // check if form is html form element and passwords match
    if (!(form instanceof HTMLFormElement)) return;

    // get input elements
    /** @type {HTMLInputElement|null} */
    const passwordInput = form.querySelector(`.${passwordClassName}_input`);
    /** @type {HTMLInputElement|null} */
    const confirmPasswordInput = form.querySelector(
      `.${confirmPasswordClassName}_input`
    );
    if (!passwordInput || !confirmPasswordInput) return;

    // get label elements and error element
    const passwordLabel = form.querySelector(`.${passwordClassName}_label`);
    const confirmPasswordLabel = form.querySelector(
      `.${confirmPasswordClassName}_label`
    );
    const confirmPasswordError = form.querySelector(
      `.${confirmPasswordClassName}_error`
    );

    // check if passwords match
    const password = passwordInput.value.trim();
    const confirmPassword = confirmPasswordInput.value.trim();
    if (password === confirmPassword) return;

    // set error and add error class to password and confirm password input, label, and error element
    this.error = true;
    passwordInput.classList.add("error");
    confirmPasswordInput.classList.add("error");
    if (passwordLabel) passwordLabel.classList.add("error");
    if (confirmPasswordLabel) confirmPasswordLabel.classList.add("error");
    if (confirmPasswordError)
      confirmPasswordError.textContent = "Passwords do not match";
  }

  /**
   * Validates a required form file field
   * Adds error styling to input, label, and displays error message if field is empty
   * @param {HTMLFormElement} form - The form element to validate
   * @param {string} className - The CSS class name prefix for the field (e.g., "filename" for .filename_input, .filename_label, .filename_error)
   * @returns {void}
   */
  nlRequiredFileField(form, className) {
    if (!(form instanceof HTMLFormElement)) return;

    // get input element
    /** @type {HTMLInputElement|null} */
    const input = form.querySelector(`.${className}_input`);
    if (!input) return;

    // get label and error elements
    const label = form.querySelector(`.${className}_label`);
    const error = form.querySelector(`.${className}_error`);

    // check if file is not empty
    if (input.files && input.files.length > 0) {
      // clear error
      input.classList.remove("error");
      if (label) label.classList.remove("error");
      if (error) error.textContent = "";
      return;
    }

    // set error and add error class to input, label, and error element
    this.error = true;
    input.classList.add("error");
    if (label) label.classList.add("error");
    if (error) error.textContent = "Required Field";
  }

  /**
   * Executes reCAPTCHA if configured
   * @param {string} recaptchaKey - The recaptcha key
   * @returns {Promise<string>} The reCAPTCHA token
   */
  async nlGetRecaptchaToken(recaptchaKey) {
    /** @type {Grecaptcha} */
    /** @ts-ignore */
    const recaptcha = window.grecaptcha;

    if (recaptchaKey != "-" && typeof recaptcha !== "undefined") {
      await new Promise((resolve) => recaptcha.ready(resolve));
      try {
        return await recaptcha.execute(recaptchaKey, { action: "submit" });
      } catch (error) {
        return "";
      }
    } else {
      return "";
    }
  }
}

/**
 * Nala Contact Form Class
 * @extends {NalaBase}
 */
class NalaContactForm extends NalaBase {
  constructor() {
    super();
    this.cookieName = "nala-contact-form-values";
    this.form = this.querySelector("form");
    this.values = JSON.parse(this.nlGetCookie(this.cookieName, "{}"));
    this.showHideBlocks = this.querySelectorAll(
      ".nala-block-contact-form--show-hide-blocks"
    );
  }

  connectedCallback() {
    this.nlInitValues();
    this.nlInitializeEvents();
  }

  nlInitValues() {
    const elements = this.form?.elements;
    if (!elements) return;
    [...elements]?.forEach((el) => {
      if (
        !(
          el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement
        )
      )
        return;

      const val = this.values[el.name] || "";
      if (val) {
        if (el instanceof HTMLInputElement && el.type === "checkbox") {
          el.checked = val !== "false";
        } else {
          el.value = val;
        }
      } else {
        let value = el.value;
        if (
          el instanceof HTMLInputElement &&
          el.type === "checkbox" &&
          !el.checked
        ) {
          value = "false";
        }
        this.values[el.name] = value;
      }
    });

    this.showHideBlocks?.forEach((block) =>
      this.nlUpdateShowHideBlocks(/** @type {HTMLSelectElement} */(block))
    );
  }

  nlInitializeEvents() {
    this.form?.addEventListener("change", (event) =>
      this.nlHandleFormChange(event)
    );
    this.showHideBlocks?.forEach((block) =>
      block.addEventListener("change", (event) =>
        this.nlHandleShowHideBlocksChange(event)
      )
    );
  }

  /**
   * Handle form change
   * @param {Event} event - The event object
   */
  nlHandleFormChange(event) {
    const target = event.target;
    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      )
    )
      return;

    let value = target.value;
    if (
      target instanceof HTMLInputElement &&
      target.type === "checkbox" &&
      !target.checked
    ) {
      value = "false";
    }

    this.values[target.name] = value.trim();
    this.nlSetCookie(this.cookieName, JSON.stringify(this.values), 1);
  }

  /**
   * Handle show/hide blocks change
   * @param {Event} event - The event object
   */
  nlHandleShowHideBlocksChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    this.nlUpdateShowHideBlocks(target);
  }

  /**
   * Update show/hide blocks
   * @param {HTMLSelectElement} target - The target element
   */
  nlUpdateShowHideBlocks(target) {
    const showHideClassName = target.dataset.showHideClassName;
    if (!showHideClassName) return;

    const showClassName = `${showHideClassName}-${target.value
      .toLowerCase()
      .replace(/ /g, "-")}`;
    const blocks = this.querySelectorAll(`.${showHideClassName}-block`);
    blocks.forEach((block) => {
      if (!(block instanceof HTMLElement)) return;

      if (block.classList.contains(showClassName)) {
        block.style.display = "block";
      } else {
        block.style.display = "none";
      }
    });
  }
}

customElements.define("nala-contact-form", NalaContactForm);

/**
 * Nala Header Menu Class
 * @extends {NalaBase}
 */
class NalaHeaderMenu extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlSetupDropdowns();
  }

  nlSetupDropdowns() {
    // Attach event listeners to all menu items with submenus
    const menuItems = this.querySelectorAll(".nala-header-menu__list-item");
    menuItems.forEach((item) => {
      const submenu = item.querySelector(".nala-header__dropdown-menu");
      if (submenu) {
        // Show submenu on mouseenter/focus
        item.addEventListener("mouseenter", () =>
          this.nlOpenDropdown(/** @type {HTMLElement} */(item))
        );
        item.addEventListener("focusin", () =>
          this.nlOpenDropdown(/** @type {HTMLElement} */(item))
        );
        // Hide submenu on mouseleave/blur
        item.addEventListener("mouseleave", () =>
          this.nlCloseDropdown(/** @type {HTMLElement} */(item))
        );
        item.addEventListener("focusout", (e) => {
          /** @type {FocusEvent} */
          const fe = /** @type {FocusEvent} */ (e);
          // Only close if focus moves outside the item
          if (!item.contains(/** @type {Node} */(fe.relatedTarget))) {
            this.nlCloseDropdown(/** @type {HTMLElement} */(item));
          }
        });

        const subMenuItems = item.querySelectorAll(
          ".nala-header-dropdown-menu__item"
        );
        subMenuItems.forEach((subItem) => {
          const submenu = subItem.querySelector(
            ".nala-header-dropdown-menu__submenu"
          );
          if (submenu) {
            // Show submenu on mouseenter/focus
            subItem.addEventListener("mouseenter", () =>
              this.nlOpenDropdownSubmenu(/** @type {HTMLElement} */(subItem))
            );
            subItem.addEventListener("focusin", () =>
              this.nlOpenDropdownSubmenu(/** @type {HTMLElement} */(subItem))
            );
            // Hide submenu on mouseleave/blur
            subItem.addEventListener("mouseleave", () =>
              this.nlCloseDropdown(/** @type {HTMLElement} */(subItem))
            );
            subItem.addEventListener("focusout", (e) => {
              /** @type {FocusEvent} */
              const fe = /** @type {FocusEvent} */ (e);
              // Only close if focus moves outside the item
              if (!subItem.contains(/** @type {Node} */(fe.relatedTarget))) {
                this.nlCloseDropdown(/** @type {HTMLElement} */(subItem));
              }
            });
          }
        });
      }
    });
  }

  /**
   * Open dropdown for a menu item and center it under the link
   * @param {HTMLElement} item
   */
  nlOpenDropdown(item) {
    item.classList.add("dropdown-open");
    const submenu = item.querySelector(".nala-header__dropdown-menu");
    if (submenu && submenu instanceof HTMLElement) {
      submenu.classList.add("dropdown-visible");

      // Center the dropdown under the link
      const link = item.querySelector(".nala-header-menu__link");
      if (link && submenu.classList.contains("nala-header__dropdown-menu")) {
        // Reset left to auto before calculation
        submenu.style.left = "auto";

        // Get bounding rectangles
        const linkRect = link.getBoundingClientRect();
        const submenuRect = submenu.getBoundingClientRect();
        const windowWidth = window.innerWidth;

        // Calculate the left offset so the dropdown is centered under the link
        let left = linkRect.width / 2 - submenuRect.width / 2;
        if (windowWidth < submenuRect.right) {
          left -= (submenuRect.right - windowWidth) / 2;
        }

        // If the dropdown is too far left, set it to 0
        if (linkRect.left + left < 0) {
          left = -linkRect.left + 1;
        }

        submenu.style.left = `${left}px`;
      }
    }
  }

  /**
   * Open dropdown for a menu item and center it under the link
   * @param {HTMLElement} item
   */
  nlOpenDropdownSubmenu(item) {
    item.classList.add("dropdown-open");
    const submenu = item.querySelector(".nala-header-dropdown-menu__submenu");
    if (submenu && submenu instanceof HTMLElement) {
      submenu.classList.add("dropdown-visible");

      // right or left align the dropdown
      const link = item.querySelector(".nala-header-dropdown-menu__link");
      if (
        link &&
        submenu.classList.contains("nala-header-dropdown-menu__submenu")
      ) {
        // Reset left to auto before calculation
        submenu.style.left = "100%";

        // Get bounding rectangles
        const linkRect = link.getBoundingClientRect();
        const submenuRect = submenu.getBoundingClientRect();
        const windowWidth = window.innerWidth;

        // If the dropdown is too far right, set it to 0
        if (
          windowWidth < submenuRect.right ||
          submenu.classList.contains("dropdown-reverse")
        ) {
          submenu.style.left = `-${linkRect.width}px`;
          submenu.classList.add("dropdown-reverse");
          submenu
            .querySelectorAll(".nala-header-dropdown-menu__submenu")
            .forEach((submenu1) => {
              submenu1.classList.add("dropdown-reverse");
            });
        }
      }
    }
  }

  /**
   * Close dropdown for a menu item
   * @param {HTMLElement} item
   */
  nlCloseDropdown(item) {
    item.classList.remove("dropdown-open");
    const submenu = item.querySelector(
      ".nala-header__dropdown-menu, .nala-header-dropdown-menu__submenu"
    );
    if (submenu) {
      submenu.classList.remove("dropdown-visible");
    }
  }
}

customElements.define("nala-header-menu", NalaHeaderMenu);

/**
 * Nala Tabs Class
 * @extends {NalaBase}
 */
class NalaTabs extends NalaBase {
  constructor() {
    super();
    this.tabs = this.querySelectorAll(".nala-block-tabs--tab");
    this.items = this.querySelectorAll(".nala-block-tabs--content-item");
  }

  connectedCallback() {
    if (!this.tabs || this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) =>
      tab.addEventListener("click", () =>
        this.nlHandleTabClick(/** @type {HTMLElement} */(tab), index)
      )
    );

    const firstTab = /** @type {HTMLElement} */ (this.tabs[0]);
    firstTab.click();
  }

  /**
   * Handle tab click
   * @param {HTMLElement} tab
   * @param {number} tabIndex
   */
  nlHandleTabClick(tab, tabIndex) {
    this.tabs.forEach((tab, index) => {
      if (index === tabIndex) {
        tab.classList.add("active");
      } else {
        tab.classList.remove("active");
      }
    });
    this.items?.forEach((item, index) => {
      if (index === tabIndex) {
        item.classList.remove("hidden");
      } else {
        item.classList.add("hidden");
      }
    });
  }
}

customElements.define("nala-tabs", NalaTabs);

class NalaSearchBox extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    if (this.settings.nlShowExternalSearch !== true) {
      this.querySelector("input")?.addEventListener("click", (event) => {
        // @ts-ignore
        document.querySelector("#search-modal")?.showDialog();
      });
    }
  }
}

customElements.define("nala-search-box", NalaSearchBox);

/**
 * Add gallery images
 */
function nlAddGalleryImages() {
  // get all gallery image divs
  const galleryDivs = document.querySelectorAll(".nala-gallery-images");
  let modalCount = 0;

  galleryDivs.forEach((galleryDiv) => {
    // get images in each gallery div
    const images = Array.from(galleryDiv.querySelectorAll(".image-block"));

    // if no images, return
    if (images.length === 0) return;

    // Create modal dialog for this gallery
    const modalId = `nala-gallery-modal-${modalCount++}`;
    const modal = nlCreateGalleryModal(modalId);
    document.body.appendChild(modal);

    // get elements from modal
    const dialog = modal.querySelector("dialog");
    const modalImage = /** @type {HTMLImageElement} */ (
      modal.querySelector(".nala-gallery-modal-image")
    );
    const prevButton = /** @type {HTMLButtonElement} */ (
      modal.querySelector(".nala-gallery-modal-prev")
    );
    const nextButton = /** @type {HTMLButtonElement} */ (
      modal.querySelector(".nala-gallery-modal-next")
    );
    const closeButton = /** @type {HTMLButtonElement} */ (
      modal.querySelector(".nala-gallery-modal-close")
    );
    const imageCounter = /** @type {HTMLDivElement} */ (
      modal.querySelector(".nala-gallery-modal-counter")
    );

    // set current index to 0
    let currentIndex = 0;

    // Function to update modal with image at index
    /**
     * @param {number} index
     */
    const nlUpdateModalImage = (index) => {
      // if index is out of bounds, return
      if (index < 0 || index >= images.length) return;

      // get image element
      const imageElement = images[index];
      if (!imageElement) return;

      // set current index
      currentIndex = index;

      // get image element
      const img = /** @type {HTMLImageElement} */ (
        imageElement.querySelector("img")
      );
      if (!img || !modalImage) return;

      // get image src
      const imgSrc =
        img.src ||
        img.getAttribute("data-src") ||
        img.getAttribute("srcset")?.split(",")[0]?.trim().split(" ")[0] ||
        "";
      const imgAlt = img.alt || "Gallery image";

      // set image src and alt
      modalImage.src = imgSrc;
      modalImage.alt = imgAlt;

      // Update navigation buttons
      if (prevButton) {
        prevButton.disabled = index === 0;
        prevButton.setAttribute(
          "aria-disabled",
          index === 0 ? "true" : "false"
        );
      }
      if (nextButton) {
        nextButton.disabled = index === images.length - 1;
        nextButton.setAttribute(
          "aria-disabled",
          index === images.length - 1 ? "true" : "false"
        );
      }

      // Update counter
      if (imageCounter) {
        imageCounter.textContent = `${index + 1} / ${images.length}`;
      }
    };

    // Function to open modal with image at index
    /**
     * @param {number} index
     */
    const nlOpenModal = (index) => {
      // if index is out of bounds, return
      if (index < 0 || index >= images.length) return;

      // update modal image
      nlUpdateModalImage(index);

      if (dialog) {
        dialog.showModal();
        // Focus management for accessibility
        if (closeButton) {
          closeButton.focus();
        }
      }
    };

    // Function to close modal
    const nlCloseModal = () => {
      if (dialog) {
        dialog.close();
      }
    };

    // Function to navigate to next image
    const nlNextImage = () => {
      if (currentIndex < images.length - 1) {
        nlUpdateModalImage(currentIndex + 1);
      }
    };

    // Function to navigate to previous image
    const nlPrevImage = () => {
      if (currentIndex > 0) {
        nlUpdateModalImage(currentIndex - 1);
      }
    };

    // Add click event listeners to each image
    images.forEach((imageElement, index) => {
      if (!(imageElement instanceof HTMLElement)) return;

      imageElement.setAttribute("role", "button");
      imageElement.setAttribute("tabindex", "0");
      imageElement.setAttribute(
        "aria-label",
        `Open gallery image ${index + 1} of ${images.length}`
      );

      imageElement.addEventListener("click", () => nlOpenModal(index));
      imageElement.addEventListener("keydown", (e) => {
        /** @type {KeyboardEvent} */
        const ke = /** @type {KeyboardEvent} */ (e);
        if (ke.key === "Enter" || ke.key === " ") {
          ke.preventDefault();
          nlOpenModal(index);
        }
      });
    });

    // Add event listeners to modal controls
    if (prevButton) {
      prevButton.addEventListener("click", nlPrevImage);
    }
    if (nextButton) {
      nextButton.addEventListener("click", nlNextImage);
    }
    if (closeButton) {
      closeButton.addEventListener("click", nlCloseModal);
    }

    // Keyboard navigation
    if (dialog) {
      dialog.addEventListener("keydown", (e) => {
        /** @type {KeyboardEvent} */
        const ke = /** @type {KeyboardEvent} */ (e);
        if (ke.key === "ArrowLeft") {
          ke.preventDefault();
          nlPrevImage();
        } else if (ke.key === "ArrowRight") {
          ke.preventDefault();
          nlNextImage();
        } else if (ke.key === "Escape") {
          ke.preventDefault();
          nlCloseModal();
        }
      });

      // Close on backdrop click
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) {
          nlCloseModal();
        }
      });
    }
  });
}

/**
 * Create gallery modal HTML structure
 * @param {string} modalId - Unique ID for the modal
 * @returns {HTMLElement} The modal element
 */
function nlCreateGalleryModal(modalId) {
  const modal = document.createElement("div");
  modal.className = "nala-gallery-modal-wrapper";

  modal.innerHTML = `
    <dialog class="nala-gallery-modal-dialog dialog-modal" id="${modalId}" aria-labelledby="${modalId}-title" aria-modal="true">
      <div class="nala-gallery-modal-content">
        <button 
          type="button" 
          class="nala-gallery-modal-close button button-unstyled close-button" 
          aria-label="Close gallery modal"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <h2 id="${modalId}-title" class="visually-hidden">Gallery Image Viewer</h2>
        
        <div class="nala-gallery-modal-image-container">
          <button 
            type="button" 
            class="nala-gallery-modal-prev button button-unstyled" 
            aria-label="Previous image"
            aria-disabled="true"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <img class="nala-gallery-modal-image" src="" alt="" loading="eager">
          
          <button 
            type="button" 
            class="nala-gallery-modal-next button button-unstyled" 
            aria-label="Next image"
            aria-disabled="false"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div class="nala-gallery-modal-counter" aria-live="polite" aria-atomic="true">1 / 1</div>
      </div>
    </dialog>
  `;

  return modal;
}

/**
 * Nala Carousel Class
 * @extends {NalaBase}
 */
class NalaCarousel extends NalaBase {
  constructor() {
    super();
    this.carouselId = this.getAttribute("data-carousel-id");
    this.track = this.querySelector(".nala-list-block__carousel-track");
    this.container = this.querySelector(".nala-list-block__carousel-container");
    this.prevButton = this.querySelector(
      ".nala-list-block__carousel-button--prev"
    );
    this.nextButton = this.querySelector(
      ".nala-list-block__carousel-button--next"
    );
    this.iconsStyle = this.getAttribute("data-icons-style") || "arrow";
    this.iconsShape = this.getAttribute("data-icons-shape") || "none";
    this.columns = parseInt(this.getAttribute("data-columns") || "4", 10);
    this.mobileCardSize = this.getAttribute("data-mobile-card-size") || "60cqw";
    this.slides = this.container ? Array.from(this.container.children) : [];
    this.currentIndex = 0;
    this.isScrolling = false;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  }

  connectedCallback() {
    if (!this.track || !this.container) return;

    this.nlInitializeCarousel();
    this.nlSetupButtons();
    this.nlSetupKeyboardNavigation();
    this.nlSetupScrollListener();
    this.nlUpdateButtonStates();
    this.nlApplyIconStyles();
  }

  nlInitializeCarousel() {
    // Set up initial scroll position
    if (!this.track) return;
    this.track.scrollLeft = 0;
    this.currentIndex = 0;

    // Calculate slide width based on viewport
    this.nlUpdateSlideWidths();
    window.addEventListener("resize", () => {
      this.nlUpdateSlideWidths();
      this.nlUpdateButtonStates();
    });
  }

  nlUpdateSlideWidths() {
    if (!this.container || !this.track || !this.slides.length) return;

    const isMobile = window.innerWidth < 750;
    if (isMobile) {
      // Mobile: use container query width
      this.slides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.style.width = `var(--nala-list-mobile-card-size, ${this.mobileCardSize})`;
        }
      });
    } else {
      // Desktop: calculate based on columns
      const containerWidth = this.track.clientWidth;
      const gap = parseInt(
        getComputedStyle(this.container).gap.replace("px", "") || "8",
        10
      );
      const slideWidth =
        (containerWidth - (this.columns - 1) * gap) / this.columns;
      this.slides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.style.width = `${slideWidth}px`;
          slide.style.minWidth = `${slideWidth}px`;
        }
      });
    }
  }

  nlSetupButtons() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.nlScrollToPrev());
      this.prevButton.setAttribute(
        "data-shape",
        this.iconsShape !== "none" ? this.iconsShape : ""
      );
      this.prevButton.setAttribute("data-style", this.iconsStyle);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.nlScrollToNext());
      this.nextButton.setAttribute(
        "data-shape",
        this.iconsShape !== "none" ? this.iconsShape : ""
      );
      this.nextButton.setAttribute("data-style", this.iconsStyle);
    }
  }

  nlApplyIconStyles() {
    const buttons = [this.prevButton, this.nextButton].filter(
      (button) => button instanceof HTMLElement
    );

    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        if (this.iconsShape === "circle") {
          button.style.borderRadius = "50%";
          button.style.background = "rgba(0, 0, 0, 0.05)";
        } else if (this.iconsShape === "square") {
          button.style.borderRadius = "0";
          button.style.background = "rgba(0, 0, 0, 0.05)";
        }
      }
    });
  }

  nlSetupKeyboardNavigation() {
    if (!this.track) return;

    this.track.setAttribute("tabindex", "0");
    this.track.addEventListener("keydown", (e) => {
      const event = /** @type {KeyboardEvent} */ (e);
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.nlScrollToPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.nlScrollToNext();
      }
    });
  }

  nlSetupScrollListener() {
    if (!this.track) return;

    this.track.addEventListener("scroll", () => {
      if (this.isScrolling) return;
      this.nlUpdateButtonStates();
    });
  }

  nlScrollToPrev() {
    if (!this.track || !this.container || this.isScrolling) return;

    const isMobile = window.innerWidth < 750;
    const gap = parseInt(
      getComputedStyle(this.container).gap.replace("px", "") || "8",
      10
    );

    if (isMobile) {
      // Mobile: scroll by one card width
      const slideWidth =
        this.track.clientWidth *
        (parseInt(this.mobileCardSize.replace("cqw", ""), 10) / 100);
      this.track.scrollBy({
        left: -(slideWidth + gap),
        behavior: this.reducedMotion.matches ? "auto" : "smooth",
      });
    } else {
      // Desktop: scroll by visible columns
      const containerWidth = this.track.clientWidth;
      const slideWidth =
        (containerWidth - (this.columns - 1) * gap) / this.columns;
      const scrollAmount = slideWidth * this.columns + gap * (this.columns - 1);
      this.track.scrollBy({
        left: -scrollAmount,
        behavior: this.reducedMotion.matches ? "auto" : "smooth",
      });
    }

    this.isScrolling = true;
    setTimeout(() => {
      this.isScrolling = false;
      this.nlUpdateButtonStates();
    }, 300);
  }

  nlScrollToNext() {
    if (!this.track || !this.container || this.isScrolling) return;

    const isMobile = window.innerWidth < 750;
    const gap = parseInt(
      getComputedStyle(this.container).gap.replace("px", "") || "8",
      10
    );

    if (isMobile) {
      // Mobile: scroll by one card width
      const slideWidth =
        this.track.clientWidth *
        (parseInt(this.mobileCardSize.replace("cqw", ""), 10) / 100);
      this.track.scrollBy({
        left: slideWidth + gap,
        behavior: this.reducedMotion.matches ? "auto" : "smooth",
      });
    } else {
      // Desktop: scroll by visible columns
      const containerWidth = this.track.clientWidth;
      const slideWidth =
        (containerWidth - (this.columns - 1) * gap) / this.columns;
      const scrollAmount = slideWidth * this.columns + gap * (this.columns - 1);
      this.track.scrollBy({
        left: scrollAmount,
        behavior: this.reducedMotion.matches ? "auto" : "smooth",
      });
    }

    this.isScrolling = true;
    setTimeout(() => {
      this.isScrolling = false;
      this.nlUpdateButtonStates();
    }, 300);
  }

  nlUpdateButtonStates() {
    if (!this.track) return;

    const scrollLeft = this.track.scrollLeft;
    const scrollWidth = this.track.scrollWidth;
    const clientWidth = this.track.clientWidth;
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    if (this.prevButton instanceof HTMLButtonElement) {
      this.prevButton.disabled = isAtStart;
      this.prevButton.setAttribute("aria-disabled", String(isAtStart));
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = isAtEnd;
      this.nextButton.setAttribute("aria-disabled", String(isAtEnd));
    }
  }
}

customElements.define("nala-carousel", NalaCarousel);

class NalaCustomerAccountLogin extends NalaBase {
  constructor() {
    super();

    this.showHeading = this.getAttribute("data-show-heading") || "default";
    this.externalId = this.getAttribute("data-external-id") || "";
    this.loginHeading = this.getAttribute("data-login-heading") || "";
    this.recoverHeading = this.getAttribute("data-recover-heading") || "";
  }

  connectedCallback() {
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.querySelector(".nala-account-login-forgot")?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        const target = /** @type {HTMLElement} */ (event.target);
        const divId = target.dataset.id;
        if (!divId) {
          return;
        }

        const div = this.querySelector(`#${divId}`);
        if (!div) {
          return;
        }
        div.classList.remove("nala-hidden");
        this.querySelector("#nala-account-login")?.classList.add("nala-hidden");
        if (this.showHeading === "external") {
          const externalDiv = document.querySelector(`#${this.externalId}`);
          if (externalDiv) {
            externalDiv.innerHTML = this.recoverHeading;
          }
        }
      }
    );

    this.querySelector(".nala-account-recover-cancel")?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        const target = /** @type {HTMLElement} */ (event.target);
        const divId = target.dataset.id;
        if (!divId) {
          return;
        }
        const div = this.querySelector(`#${divId}`);
        if (!div) {
          return;
        }
        div.classList.remove("nala-hidden");
        this.querySelector("#nala-account-recover")?.classList.add(
          "nala-hidden"
        );
        if (this.showHeading === "external") {
          const externalDiv = document.querySelector(`#${this.externalId}`);
          if (externalDiv) {
            externalDiv.innerHTML = this.loginHeading;
          }
        }
      }
    );
  }
}

customElements.define("nala-customer-account-login", NalaCustomerAccountLogin);

class NalaCustomerAddresses extends NalaBase {
  constructor() {
    super();
    this.deleteConfirm = "";
    this.addressUrl = "";
  }

  connectedCallback() {
    this.deleteConfirm = this.dataset.deleteConfirm || "";
    this.addressUrl = this.dataset.addressUrl || "";
    this.nlLoadProvinces();
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.querySelector(
      ".nala-customer-addresses__form .country"
    )?.addEventListener("change", () => {
      this.nlLoadProvinces();
    });

    this.querySelector(
      ".nala-customer-addresses__add-address"
    )?.addEventListener("click", () => {
      this.nlUpdateForm();
    });

    this.querySelector(
      '.nala-customer-addresses__form button[type="reset"]'
    )?.addEventListener("click", (event) => {
      event.preventDefault();
      this.querySelector(".nala-customer-addresses__form")?.classList.add(
        "nala-hidden"
      );
      this.querySelector(
        ".nala-customer-addresses__list-container"
      )?.classList.remove("nala-hidden");
    });

    this.querySelectorAll(".nala-customer-addresses__item").forEach(
      (/** @type {HTMLElement} */ item) => {
        const address = JSON.parse(item.dataset.address || "{}");
        if (!address) {
          return;
        }
        item
          .querySelector(".nala-customer-addresses__edit")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            this.nlUpdateForm(address);
          });
        item
          .querySelector(".nala-customer-addresses__delete")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            this.nlDeleteAddress(address);
          });
      }
    );
  }

  /**
   * Load the provinces
   */
  nlLoadProvinces() {
    /** @type {HTMLSelectElement|null} */
    const country = this.querySelector(
      ".nala-customer-addresses__form .country"
    );
    if (!country) {
      return;
    }
    const selectedOption = country.options[country.selectedIndex];
    try {
      /** @type {Array<[string, string]>} */
      const provinces = JSON.parse(selectedOption?.dataset?.provinces || "[]");
      const provinceContainer = this.querySelector(
        ".nala-customer-addresses__form .province-field"
      );
      const provinceSelect = this.querySelector(
        ".nala-customer-addresses__form .province"
      );
      if (!provinceContainer || !provinceSelect) {
        return;
      }

      if (provinces.length === 0) {
        provinceContainer.classList.add("nala-hidden");
        provinceSelect.innerHTML = "";
        return;
      }

      provinceSelect.innerHTML = provinces
        .map(
          (province) => `<option value="${province[0]}">${province[1]}</option>`
        )
        .join("");
      provinceContainer.classList.remove("nala-hidden");
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {any} address
   */
  nlUpdateForm(address = {}) {
    this.querySelector(".nala-customer-addresses__form")?.classList.remove(
      "nala-hidden"
    );
    this.querySelector(
      ".nala-customer-addresses__list-container"
    )?.classList.add("nala-hidden");
    this.querySelector(".nala-customer-addresses__form-new")?.classList.remove(
      "nala-hidden"
    );
    this.querySelector(".nala-customer-addresses__form-edit")?.classList.add(
      "nala-hidden"
    );

    /** @type {HTMLFormElement|null} */
    const form = this.querySelector(".nala-customer-addresses__form form");
    if (form) {
      form.reset();
      if (address.id) {
        form.action = `${this.addressUrl}/${address.id}`;
        this.nlSetFieldValue(form, ".first-name", address.first_name);
        this.nlSetFieldValue(form, ".last-name", address.last_name);
        this.nlSetFieldValue(form, ".company", address.company);
        this.nlSetFieldValue(form, ".address1", address.address1);
        this.nlSetFieldValue(form, ".address2", address.address2);
        this.nlSetFieldValue(form, ".city", address.city);
        this.nlSetFieldValue(form, ".zip", address.zip);
        this.nlSetFieldValue(form, ".phone", address.phone);
        this.nlSetFieldValue(form, ".country", address.country);
        this.nlLoadProvinces();
        this.nlSetFieldValue(form, ".province", address.province);
        this.nlSetCheckboxChecked(
          form,
          ".default-address",
          address.default == true
        );
        this.nlSetFieldValue(form, ".method-name", "put");
      } else {
        this.nlSetFieldValue(form, ".method-name", "post");
        form.action = this.addressUrl;
        this.nlLoadProvinces();
      }
    }
  }

  /**
   * @param {any} address
   */
  nlDeleteAddress(address) {
    window.confirm(this.deleteConfirm) &&
      fetch(`${this.addressUrl}/${address.id}`, {
        method: "post",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: "_method=delete",
      }).then(() => {
        window.location.reload();
      });
  }
}

customElements.define("nala-customer-addresses", NalaCustomerAddresses);

/**
 * Image Carousel Component
 * Displays a carousel of images with navigation and optional popup support.
 * @class NalaImageCarousel
 * @extends {NalaBase}
 */
class NalaImageCarousel extends NalaBase {
  constructor() {
    super();
    this.currentSlide = 0;
    this.slidesPerView = 5;
    this.totalSlides = 10;
    this.maxSlide = 0;
    this.remSlides = 0;
    this.imageLoop = false;
    this.imagePopup = false;
    this.screenType = "desktop";
    this.updateMobile = true;
  }

  /**
   * @description Called when the element is added to the DOM
   */
  connectedCallback() {
    this.totalSlides = Number(this.dataset.totalSlides || 10);
    this.mobileImages = Number(this.dataset.mobileImages || 2);
    this.tabletImages = Number(this.dataset.tabletImages || 3);
    this.desktopImages = Number(this.dataset.desktopImages || 5);
    this.imageLoop = this.dataset.imageLoop === "true";
    this.imagePopup = this.dataset.imagePopup === "true";

    this.nlInitializeEvents();
  }

  /**
   * @description Initialize carousel DOM references and events
   */
  nlInitializeEvents() {
    /** @type {HTMLDivElement|null} */
    this.track = this.querySelector(".nala-image-carousel-track");
    /** @type {HTMLButtonElement|null} */
    this.prevButton = this.querySelector(".nala-image-carousel-nav-prev");
    /** @type {HTMLButtonElement|null} */
    this.nextButton = this.querySelector(".nala-image-carousel-nav-next");
    this.dots = this.querySelectorAll(".nala-image-carousel-dot");
    this.images = this.querySelectorAll(".nala-image");
    this.counter = this.querySelector(".nala-image-carousel-counter");

    this.nlUpdateSlidesPerView();
    this.nlSetupEventListeners();
    this.nlUpdateCarousel();
  }

  /**
   * @description Update the number of slides per view based on the window width
   */
  nlUpdateSlidesPerView() {
    const width = window.innerWidth;
    if (width <= 749) {
      this.slidesPerView = Number(this.mobileImages);
      this.screenType = "mobile";
    } else if (width <= 990) {
      this.slidesPerView = Number(this.tabletImages);
      this.screenType = "tablet";
    } else {
      this.slidesPerView = Number(this.desktopImages);
      this.screenType = "desktop";
    }
    this.maxSlide = Math.ceil(this.totalSlides / this.slidesPerView) - 1;
    this.remSlides = this.totalSlides - this.maxSlide * this.slidesPerView;
    this.dots?.forEach((dot, index) => {
      dot.classList.toggle(
        "nala-hidden",
        this.maxSlide > 0 ? index > this.maxSlide : true
      );
    });
    if (this.maxSlide == 0) {
      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
    } else {
      if (this.prevButton) this.prevButton.disabled = false;
      if (this.nextButton) this.nextButton.disabled = false;
    }
  }

  /**
   * @description Set up event listeners for navigation buttons and dots
   */
  nlSetupEventListeners() {
    this.prevButton?.addEventListener("click", () => this.nlGoToPrevSlide());
    this.nextButton?.addEventListener("click", () => this.nlGoToNextSlide());

    this.dots?.forEach((dot, index) => {
      dot.addEventListener("click", () => this.nlGoToSlide(index));
    });

    if (this.imagePopup) {
      this.images?.forEach((image, index) => {
        image.addEventListener("click", () => this.nlOpenImagePopup(index));
      });
    }

    window.addEventListener("resize", () => {
      this.nlUpdateSlidesPerView();
      if (this.currentSlide > this.maxSlide) {
        this.currentSlide = this.maxSlide;
      }
      this.nlUpdateCarousel();
    });
  }

  /**
   * @description Navigate to the previous slide
   */
  nlGoToPrevSlide() {
    if (this.imageLoop == false && this.currentSlide <= 0) return;
    this.currentSlide--;
    if (this.currentSlide < 0) {
      this.currentSlide = this.maxSlide;
    }
    this.nlUpdateCarousel();
  }

  /**
   * @description Navigate to the next slide
   */
  nlGoToNextSlide() {
    if (this.imageLoop == false && this.currentSlide >= this.maxSlide) return;
    this.currentSlide++;
    if (this.currentSlide > this.maxSlide) {
      this.currentSlide = 0;
    }
    this.nlUpdateCarousel();
  }

  /**
   * @description Navigate to a specific slide
   * @param {number} slideIndex
   */
  nlGoToSlide(slideIndex) {
    if (slideIndex >= 0 && slideIndex <= this.maxSlide) {
      this.currentSlide = slideIndex;
      this.nlUpdateCarousel();
    }
  }

  /**
   * @description Update the carousel's position and button states
   */
  nlUpdateCarousel() {
    let translateX = this.currentSlide * 100;
    if (this.currentSlide == this.maxSlide) {
      translateX =
        (this.currentSlide - 1) * 100 +
        (this.remSlides * 100) / this.slidesPerView;
    }
    if (this.screenType == "mobile" && this.updateMobile) {
      translateX = translateX + this.currentSlide;
    }
    if (this.track) {
      this.track.style.transform = `translateX(-${translateX}%)`;
    }

    if (this.imageLoop == false) {
      if (this.prevButton) {
        this.prevButton.disabled = this.currentSlide === 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.currentSlide === this.maxSlide;
      }
    }

    this.dots?.forEach((dot, index) => {
      dot.classList.toggle("active", index === this.currentSlide);
    });

    if (this.counter) {
      const current = this.counter.querySelector(".current");
      const total = this.counter.querySelector(".total");
      if (current) {
        current.textContent = `${this.currentSlide + 1}`;
      }
      if (total) {
        total.textContent = `${this.totalSlides}`;
      }
    }
  }

  /**
   * @description Open the image popup with the given index
   * @param {number} index
   * @returns
   */
  nlOpenImagePopup(index) {
    if (!this.imagePopup) return;

    let popup = document.querySelector("nala-image-carousel-popup");
    if (!popup) {
      popup = document.createElement("nala-image-carousel-popup");
      document.body.appendChild(popup);
    }

    // @ts-ignore
    popup.nlOpenPopup(this.images, index);
  }
}

customElements.define("nala-image-carousel", NalaImageCarousel);

/**
 * Popup Image Carousel Component
 * Displays images in a popup carousel overlay, extending the base image carousel.
 * @class NalaPopupImageCarousel
 * @extends {NalaImageCarousel}
 */
class NalaPopupImageCarousel extends NalaImageCarousel {
  constructor() {
    super();
    this.mobileImages = 1;
    this.tabletImages = 1;
    this.desktopImages = 1;
    this.imageLoop = false;
    this.imagePopup = false;
    this.popupImages = /** @type {HTMLDivElement[]} **/ ([]);
    this.updateMobile = false;
  }

  /**
   * @description Called when the element is added to the DOM
   */
  connectedCallback() { }

  /**
   * @description Open popup
   * @param {NodeListOf<HTMLDivElement>} images
   * @param {number} index
   * @param {{[key: string]: string}} params
   */
  nlOpenPopup(images, index, params = {}) {
    const imagesArray = Array.from(images);
    let popupImage = null;
    let counter = 0;
    this.popupImages = [];
    imagesArray.forEach((image, index1) => {
      if (image.classList.contains("nala-hidden")) {
        return;
      }

      if (index === index1) {
        popupImage = image;
        this.currentSlide = counter;
      }
      this.popupImages.push(image);
      counter++;
    });
    this.totalSlides = Number(this.popupImages.length);

    this.nlCreateHtml();
    this.nlInitializeEvents();
    this.classList.add("active");
  }

  /**
   * @description close popup
   */
  nlClosePopup() {
    this.classList.remove("active");
  }

  /**
   * @description initialize events
   */
  nlInitializeEvents() {
    this.modal = this.querySelector(".nala-image-carousel-popup");
    this.closeButton = this.querySelector(".nala-image-carousel-close");

    super.nlInitializeEvents();
  }

  nlSetupEventListeners() {
    this.addEventListener("click", (event) => this.nlHandleClickOutside(event));
    document.addEventListener("keydown", (event) =>
      this.nlHandleEscapeKey(event)
    );
    this.closeButton?.addEventListener("click", () => this.nlClosePopup());

    super.nlSetupEventListeners();
  }

  /**
   * @description Handle escape key
   * @param {KeyboardEvent} event
   */
  nlHandleEscapeKey(event) {
    if (event.key === "Escape") this.nlClosePopup();
  }

  /**
   * @description Handle click outside
   * @param {MouseEvent} event
   */
  nlHandleClickOutside(event) {
    if (event.target === this.modal || event.target === this)
      this.nlClosePopup();
  }

  /**
   * @description Create the HTML structure for the popup image carousel
   */
  nlCreateHtml() {
    const html = `
      <div class="nala-image-carousel-popup">
        <div class="nala-image-carousel-container">
          <div class="nala-image-carousel-track">${this.popupImages
        ?.map((image) => {
          const img = /** @type {HTMLImageElement} */ (
            image.querySelector("img")
          );
          const overlay = /** @type {HTMLDivElement} */ (
            image.querySelector(".nala-image__overlay")
          );
          const imgsrc = image.dataset.imageSrc || img?.src || "";
          if (!imgsrc) {
            return ``;
          }
          return `
              <div class="nala-image">
                <img src="${imgsrc || img?.src || ""}" />
                ${overlay
              ? `<div class="nala-image__overlay">${overlay.innerHTML}</div>`
              : ""
            }
              </div>
            `;
        })
        .join("")}</div>
          
          <button
            class="nala-image-carousel-nav nala-image-carousel-nav-prev"
            aria-label="Previous image"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </button>
          <button
            class="nala-image-carousel-nav nala-image-carousel-nav-next"
            aria-label="Next image"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </button>
        </div>

        <button
          class="nala-image-carousel-close"
          aria-label="Close popup"
          type="button"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div class="nala-image-carousel-counter">
          <span class="current">1</span> / <span class="total">1</span>
        </div>
      </div>
    `;
    this.innerHTML = html;
  }
}

customElements.define("nala-image-carousel-popup", NalaPopupImageCarousel);

/**
 * Image Collage Component
 * Displays a collage of images with tab filtering and optional popup support.
 * @class NalaImageCollage
 * @extends {NalaBase}
 */
class NalaImageCollage extends NalaBase {
  constructor() {
    super();
    this.imagePopup = false;
  }

  /**
   * @description Called when the element is added to the DOM
   */
  connectedCallback() {
    this.imagePopup = this.dataset.imagePopup === "true";
    this.nlInitializeEvents();
  }

  /**
   * @description Initialize image and tab event listeners for collage
   */
  nlInitializeEvents() {
    this.images = this.querySelectorAll(".nala-image");
    this.tabs = this.querySelectorAll(".nala-image-collage__tab");
    this.nlSetupEventListeners();
  }

  /**
   * @description Set up event listeners for image popup and tab switching
   */
  nlSetupEventListeners() {
    if (this.imagePopup) {
      this.images?.forEach((image, index) => {
        image.addEventListener("click", () => this.nlOpenImagePopup(index));
      });
    }

    this.tabs?.forEach((tab) => {
      tab.addEventListener("click", (event) => this.nlTabClick(event));
    });
  }

  /**
   * @description Handle tab click to filter images in collage
   * @param {Event} event
   */
  nlTabClick(event) {
    const tab = /** @type {HTMLDivElement} */ (event.target);
    const tabtype = tab.dataset.tabtype;
    this.images?.forEach((/** @type {HTMLDivElement} */ image) => {
      const imgTabtype = image.dataset.tabtype;
      if (tabtype == "all" || tabtype == imgTabtype) {
        image.classList.remove("nala-hidden");
      } else {
        image.classList.add("nala-hidden");
      }
    });
    this.tabs?.forEach((/** @type {HTMLDivElement} */ tab1) => {
      if (tabtype == tab1.dataset.tabtype) {
        tab1.classList.add("active");
      } else {
        tab1.classList.remove("active");
      }
    });
  }

  /**
   * @description Open the image popup with the given index
   * @param {number} index
   * @returns
   */
  nlOpenImagePopup(index) {
    if (!this.imagePopup) return;

    let popup = document.querySelector("nala-image-carousel-popup");
    if (!popup) {
      popup = document.createElement("nala-image-carousel-popup");
      document.body.appendChild(popup);
    }

    // @ts-ignore
    popup.nlOpenPopup(this.images, index);
  }
}

customElements.define("nala-image-collage", NalaImageCollage);

/**
 * Wishlist Component
 * @class NalaWishlist
 * @extends {NalaBase}
 */
class NalaWishlist extends NalaBase {
  constructor() {
    super();
    this.searchUrl = "/search";
  }

  /**
   * @description Get the wishlist from localStorage
   * @returns {string|null}
   */
  nlGetWishlist() {
    return (
      localStorage.getItem("nala_wishlist") || localStorage.getItem("nt_wis")
    );
  }

  /**
   * @description Get the wishlist array from localStorage
   * @returns {string[]}
   */
  nlGetWishlistArray() {
    let wish_storage = this.nlGetWishlist();
    if (wish_storage == null) {
      wish_storage = "";
    }
    return wish_storage.split(",");
  }

  /**
   * @description Set the wishlist in localStorage
   * @param {string} wishlist
   */
  nlSetWishlist(wishlist) {
    localStorage.setItem("nala_wishlist", wishlist);
    localStorage.removeItem("nt_wis");
  }

  /**
   * @description Add an item to the wishlist
   * @param {string} id
   */
  nlAddWishlist(id) {
    let storages = this.nlGetWishlistArray();
    storages.push(`id:${id}`);
    this.nlSetWishlist(storages.join(","));
  }

  /**
   * @description Remove an item from the wishlist
   * @param {string} id
   */
  nlRemoveWishlist(id) {
    const storages = this.nlGetWishlistArray().filter(
      (item) => item != `id:${id}`
    );
    this.nlSetWishlist(storages.join(","));
  }

  /**
   * @description Check if an item exists in the wishlist
   * @param {string} id
   * @returns {boolean}
   */
  nlExistsWishlist(id) {
    const storages = this.nlGetWishlistArray().filter(
      (item) => item == `id:${id}`
    );
    return storages.length == 1;
  }

  /**
   * @description Get the count of items in the wishlist
   * @returns {number}
   */
  nlGetWishlistCount() {
    const storages = this.nlGetWishlistArray().filter((item) => item != "");
    return storages.length;
  }

  /**
   * @description Get the products in the wishlist
   * @returns {Promise<any[]>}
   */
  async nlGetWishlistProducts() {
    try {
      const wish_storage = this.nlGetWishlist();
      if (wish_storage == null) {
        return [];
      }
      const q = wish_storage.replace(/,/g, " OR ");
      const url = `${this.searchUrl
        }?view=nala&type=product&options[unavailable_products]=last&q=${encodeURI(
          q
        )}`;
      const response = await fetch(url);
      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html"
      );
      const element = html.getElementById("nalaSearch");
      if (element) {
        return JSON.parse(element.innerText);
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }
}

/**
 * Wishlist Icon Component
 * @class NalaWishlistIcon
 * @extends {NalaWishlist}
 */
class NalaWishlistIcon extends NalaWishlist {
  constructor() {
    super();
  }

  /**
   * @description Called when the element is added to the DOM
   */
  connectedCallback() {
    const bubble = /** @type {HTMLDivElement} */ (
      document.querySelector(".nala-wishlist-icon-bubble")
    );
    if (bubble) {
      const count = this.nlGetWishlistCount();
      if (count > 0) {
        bubble.innerHTML = `<span aria-hidden="true">${count}</span>`;
        bubble.style.display = "flex";
      }
    }
    this.nlSetupEventListeners();
  }

  /**
   * @description Set up event listeners
   */
  nlSetupEventListeners() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      window.location.href = this.settings.nlWishlistLink;
    });
  }
}

customElements.define("nala-wishlist-icon", NalaWishlistIcon);

class NalaSearchPopup extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.closeButton = this.querySelector(".nala-search-popup__close");
    this.overlay = this.querySelector(".nala-search-popup__overlay");
    this.container = this.querySelector(".nala-search-popup__container");
    this.input = this.querySelector(".nala-search-popup__input");

    this.nlSetupEventListeners();
  }

  nlSetupEventListeners() {
    // Close button click
    this.closeButton?.addEventListener("click", () => this.nlClosePopup());

    // Overlay click
    this.overlay?.addEventListener("click", () => this.nlClosePopup());

    // Escape key
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.classList.contains("open")) {
        this.nlClosePopup();
      }
    });

    // Prevent container clicks from closing popup
    this.container?.addEventListener("click", (event) => {
      event.stopPropagation();
    });
  }

  nlOpenPopup() {
    this.classList.add("open");
    // Focus the input when popup opens
    setTimeout(() => {
      /** @type {HTMLInputElement} */ (this.input)?.focus();
    }, 100);

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  nlClosePopup() {
    this.classList.remove("open");
    // Restore body scroll
    document.body.style.overflow = "";

    // Clear input
    if (this.input) {
      /** @type {HTMLInputElement} */ (this.input).value = "";
    }
  }
}

customElements.define("nala-search-popup", NalaSearchPopup);

class NalaSearchButton extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      // @ts-ignore
      document.querySelector("nala-search-popup")?.nlOpenPopup();
    });
  }
}

customElements.define("nala-search-button", NalaSearchButton);

class NalaFooterMobileMenu extends NalaBase {
  constructor() {
    super();
    this.atBottomTime = Date.now();
  }

  connectedCallback() {
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    window.addEventListener("scroll", this.nlCheckScrollBottom.bind(this));
    window.addEventListener("resize", this.nlCheckScrollBottom.bind(this));
    this.nlCheckScrollBottom();
  }

  nlCheckScrollBottom() {
    // Use a buffer to prevent flicker
    const buffer = 50; // px
    const atBottom =
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - buffer;

    // Only update if state changes
    if (atBottom && Date.now() - this.atBottomTime > 1000) {
      this.classList.add("at-bottom");
      this.atBottomTime = Date.now();
    } else if (!atBottom && Date.now() - this.atBottomTime > 500) {
      this.classList.remove("at-bottom");
    }
  }
}

customElements.define("nala-footer-mobile-menu", NalaFooterMobileMenu);

class NalaDistributors extends NalaBase {
  constructor() {
    super();
    this.activeCategory = "default";
    /** @type {{[key: string]: {[key: string]: Distributor[]}}} */
    this.distributors = {};
    /** @type {Distributor[]} */
    this.allDistributors = [];
  }

  async connectedCallback() {
    await this.nlUpdateDistributors();
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.nlUpdateTabs();
    this.nlUpdateMap();
  }

  nlUpdateTabs() {
    const tabs = this.querySelectorAll(".nala-distributors__tab");
    if (tabs.length === 0) {
      return;
    }
    tabs.forEach((/** @type {HTMLElement} */ tab) => {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        this.activeCategory = tab.dataset.tab || "default";
        this.nlUpdateDistributors();
        tabs.forEach((/** @type {HTMLElement} */ tab1) => {
          tab1.classList.remove("active");
        });
        tab.classList.add("active");
      });
    });
  }

  async nlUpdateDistributors() {
    try {
      const listDiv = /** @type {HTMLElement} */ (
        this.querySelector(".nala-distributors__list")
      );
      if (!listDiv) {
        return;
      }
      let html = "";
      const distributors = await this.nlGetDistributors();
      this.allDistributors = [];
      for (const zone in distributors) {
        const zoneDistributors = distributors[zone];
        if (!zoneDistributors) {
          continue;
        }
        html += this.nlGetDistributorHtml(
          listDiv,
          zone.toLowerCase(),
          zoneDistributors
        );
        zoneDistributors.forEach((distributor) =>
          this.allDistributors.push(distributor)
        );
      }
      listDiv.innerHTML = html;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @returns {Promise<{[key: string]: Distributor[]}>}
   */
  async nlGetDistributors() {
    if (this.distributors[this.activeCategory]) {
      return this.distributors[this.activeCategory] || {};
    }
    this.distributors[this.activeCategory] = {};
    try {
      const response = await fetch(
        `/apps/nala-distributor/category/${this.activeCategory}`
      );
      /** @type {DistributorResponse} */
      const data = await response.json();
      this.distributors[this.activeCategory] = data.distributors;
    } catch (error) {
      console.error(error);
    }
    return this.distributors[this.activeCategory] || {};
  }

  /**
   * @param {HTMLElement} listDiv
   * @param {string} zone
   * @param {Distributor[]} distributors
   * @returns {string}
   */
  nlGetDistributorHtml(listDiv, zone, distributors) {
    const html = `
      <div class="nala-distributors__distributor">
        <div class="nala-distributors__distributor-content">
          <div class="nala-distributors__distributor-title">
            <img src="${listDiv.dataset[`${zone}Icon`]
      }" alt="zone icon" class="lazyload" />
            <h3>${listDiv.dataset[`${zone}Title`]}</h3>
          </div>
          <div class="nala-distributors__distributor-list">
            ${distributors
        .map((distributor) => {
          return `
                <div class="nala-distributors__distributor-item">
                  <div class="nala-distributors__distributor-item-image">
                    ${distributor.link
              ? `<a href="${distributor.link}" target="_blank">`
              : ""
            }
                    <img src="${distributor.image || this.dataset.defaultIcon
            }" alt="${distributor.title}" class="lazyload" />
                    ${distributor.link ? `</a>` : ""}
                  </div>
                  <div class="nala-distributors__distributor-item-content">
                    <h4>${distributor.link
              ? `<a href="${distributor.link}" target="_blank">${distributor.title}</a>`
              : distributor.title
            }</h4>
                    <p>${distributor.address}</p>
                    <p>${distributor.details}</p>
                    ${distributor.phones
              .map(
                (phone) =>
                  `<p class="phone">${this.nlGetIcon(
                    "phone",
                    24
                  )}<a href="tel:${phone}">${phone}</a></p>`
              )
              .join("")}
                    <div class="nala-distributors__distributor-item-socials">
                      ${distributor.emails
              .map(
                (email) =>
                  `<a class="email" href="mailto:${email}">${this.nlGetIcon(
                    "email",
                    24
                  )}</a>`
              )
              .join("")}
                      ${distributor.websites
              .map(
                (website) =>
                  `<a class="website" href="${website}" target="_blank">${this.nlGetIcon(
                    "website",
                    24
                  )}</a>`
              )
              .join("")}
                      ${distributor.socials
              .map((social) => {
                return `
                          <a class="${social.type}" href="${social.value
                  }" target="_blank">${this.nlGetIcon(
                    social.type,
                    24
                  )}</a>
                        `;
              })
              .join("")}
                    </div>
                  </div>
                </div>
              `;
        })
        .join("")}
            </div>
          </div>
        </div>
      </div>
    `;
    return html;
  }

  /**
   * Update the map with the distributors
   */
  nlUpdateMap() {
    // @ts-ignore
    if (!window.google || !window.google.maps) {
      return;
    }

    const _center = { lat: -25.344, lng: 131.036 };
    const custom_styles = [
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#193341" }],
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#2c5a71" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#29768a", lightness: -37 }],
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: "#406d80" }],
      },
      {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#406d80" }],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [
          { visibility: "on", color: "#3e606f", weight: 2, gamma: 0.84 },
        ],
      },
      {
        elementType: "labels.text.fill",
        stylers: [{ color: "#ffffff" }],
      },
      {
        featureType: "administrative",
        elementType: "geometry",
        stylers: [{ weight: 0.6, color: "#1a3541" }],
      },
      {
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#2c5a71" }],
      },
      {
        elementType: "labels.text.fill",
        stylers: [
          { color: "#ffffff" }, // or a color close to your map background
          { lightness: 20 },
          { gamma: 0.5 },
          { weight: 0.5 },
        ],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [
          { color: "#3e606f" },
          { weight: 2 },
          { gamma: 0.84 },
          { visibility: "on" },
          { opacity: 0.2 }, // add this for more transparency
        ],
      },
    ];

    // @ts-ignore
    const map = new google.maps.Map(
      document.getElementById("nala-distributors__map"),
      {
        zoom: 12,
        center: _center,
        styles: custom_styles,
      }
    );

    // @ts-ignore
    const bounds = new google.maps.LatLngBounds();

    const markersOnMap = this.allDistributors
      .filter(
        (distributor) =>
          distributor.latitude != "" && distributor.longitude != ""
      )
      .map((distributor) => {
        return {
          placeName: distributor.title,
          address: distributor.address,
          LatLng: [
            {
              lat: parseFloat(distributor.latitude),
              lng: parseFloat(distributor.longitude),
            },
          ],
        };
      });
    let contentString;

    /** @type {any[]} */
    let InforObj = [];

    for (let i = 0; i < markersOnMap.length; i++) {
      // content string:
      contentString = '<div class="map-info-window">';

      if (markersOnMap[i]?.placeName) {
        contentString +=
          '<h4 class="map-title">' + markersOnMap[i]?.placeName + "</h4>";
      }
      if (markersOnMap[i]?.address) {
        contentString +=
          '<div class="map-address">' + markersOnMap[i]?.address + "</div>";
      }

      contentString += "</div>";
      // @ts-ignore
      const marker = new google.maps.Marker({
        position: markersOnMap[i]?.LatLng[0],
        map: map,
      });

      // @ts-ignore
      const infowindow = new google.maps.InfoWindow({
        content: contentString,
        maxWidth: 400,
      });

      marker.addListener("click", () => {
        this.nlCloseGoogleMapOtherInfo(InforObj);
        infowindow.open(marker.get("map"), marker);
        InforObj[0] = infowindow;
      });

      bounds.extend(
        // @ts-ignore
        new google.maps.LatLng(
          markersOnMap[i]?.LatLng[0]?.lat,
          markersOnMap[i]?.LatLng[0]?.lng
        )
      );
    }

    if (markersOnMap.length > 1) {
      map.fitBounds(bounds);
      map.panToBounds(bounds);
    } else {
      map.setCenter(bounds.getCenter());
    }

    // @ts-ignore
    google.maps.event.addListener(map, "click", () => {
      // @ts-ignore
      this.nlCloseGoogleMapAllInfo(InforObj);
    });
  }

  /**
   * Close the other info
   * @param {any[]} InforObj
   */
  nlCloseGoogleMapOtherInfo(InforObj) {
    if (InforObj.length > 0) {
      /* detach the info-window from the marker ... undocumented in the API docs */
      InforObj[0]?.set("marker", null);
      /* and close it */
      InforObj[0]?.close();
      /* blank the array */
      InforObj.length = 0;
    }
  }

  /**
   * Close the all info
   * @param {any[]} InforObj
   */
  nlCloseGoogleMapAllInfo(InforObj) {
    if (InforObj.length > 0) {
      /* detach the info-window from the marker ... undocumented in the API docs */
      InforObj[0]?.set("marker", null);
      /* and close it */
      InforObj[0]?.close();
      /* blank the array */
      InforObj.length = 0;
    }
  }
}

customElements.define("nala-distributors", NalaDistributors);

class NalaCustomerWishlists extends NalaWishlist {
  constructor() {
    super();
    /** @type {any[]} */
    this.wishlistProducts = [];
  }

  async connectedCallback() {
    this.search_url = this.dataset.searchUrl || "/search";
    this.txtAddToCart = this.dataset.txtAddToCart || "Add to cart";
    this.txtRemove = this.dataset.txtRemove || "Remove";

    await this.nlAddWishlistHtml();
    this.nlInitializeEvents();
  }

  nlInitializeEvents() {
    this.querySelectorAll(".nala-customer-wishlists__item").forEach(
      (/** @type {HTMLElement} */ item) => {
        const product = this.wishlistProducts.find(
          (product) => product.id == item.dataset.productId
        );
        item
          .querySelector(".nala-customer-wishlists__item-add-to-cart")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            if (product.variants.length == 1) {
              this.nlAddToCart(product.id, [{ id: product.variants[0].id, quantity: 1 }]);
            } else {
              this.nlOpenLink(product.url, "_blank");
            }
          });
        item
          .querySelector(".nala-customer-wishlists__item-remove")
          ?.addEventListener("click", (event) => {
            event.preventDefault();
            this.nlRemoveWishlist(product.id);
            item.remove();
            this.wishlistProducts = this.wishlistProducts.filter(
              (p) => p.id != product.id
            );
            this.nlUpdateNoWishlistProducts();
          });
      }
    );
  }

  async nlAddWishlistHtml() {
    const list = this.querySelector(".nala-customer-wishlists__list");
    if (!list) {
      return;
    }
    const html = await this.nlGetWishlistHtml();
    if (this.wishlistProducts.length > 0) {
      list.innerHTML = html;
    } else {
      list.classList.add("nala-hidden");
      this.querySelector(".nala-customer-wishlists__empty")?.classList?.remove(
        "nala-hidden"
      );
    }
  }

  nlUpdateNoWishlistProducts() {
    if (this.wishlistProducts.length == 0) {
      this.querySelector(".nala-customer-wishlists__list")?.classList?.add(
        "nala-hidden"
      );
      this.querySelector(".nala-customer-wishlists__empty")?.classList?.remove(
        "nala-hidden"
      );
    }
  }

  async nlGetWishlistHtml() {
    /** @type {any[]} */
    this.wishlistProducts = await this.nlGetWishlistProducts();
    return this.wishlistProducts
      .map((product) => {
        return `
        <div class="nala-customer-wishlists__item" data-product-id="${product.id
          }">
          <div class="nala-customer-wishlists__item-image">
            <a href="${product.url}" target="_blank">
              <img src="${product.image}" alt="${product.title}">
            </a>
          </div>
          <div class="nala-customer-wishlists__item-content">
            <a href="${product.url}" target="_blank">
              <p class="nala-customer-wishlists__item-title">${product.title
          }</p>
              <p class="nala-customer-wishlists__item-price">${this.nlFormatMoney(
            product.price
          )}</p>
            </a>
          </div>
          <div class="nala-customer-wishlists__item-actions">
            <button class="button button-primary nala-customer-wishlists__item-add-to-cart">${this.txtAddToCart
          }</button>
            <button class="button button-secondary nala-customer-wishlists__item-remove">${this.txtRemove
          }</button>
          </div>
        </div>
      `;
      })
      .join("");
  }
}

customElements.define("nala-customer-wishlists", NalaCustomerWishlists);

class NalaPriceRangeSliderFacetComponent extends NalaBase {
  constructor() {
    super();

    /** @type {HTMLElement|null} */
    this.slider = this.querySelector(".nala-price-slider-facet__wrapper");
    /** @type {HTMLInputElement|null} */
    this.minRange = this.querySelector(".range--min");
    /** @type {HTMLInputElement|null} */
    this.maxRange = this.querySelector(".range--max");
    /** @type {HTMLInputElement|null} */
    this.minBubble = this.querySelector(".price-bubble--min");
    /** @type {HTMLInputElement|null} */
    this.maxBubble = this.querySelector(".price-bubble--max");

    // Initialize values
    this.min = Number(this.minRange?.min || 0);
    this.max = Number(this.minRange?.max || 0);
  }

  connectedCallback() {
    this.nlSetupEventListeners();
    this.nlUpdateBubbles();

    this.observer = new MutationObserver((mutations) => {
      if (this.slider && !this.slider.style.getPropertyValue("--min-percent")) {
        this.nlUpdateBubbles();
      }
    });
    // @ts-ignore
    this.observer.observe(this.parentNode, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * Setup the event listeners
   */
  nlSetupEventListeners() {
    this.minRange?.addEventListener("input", () => {
      this.nlValidateRanges("min");
      this.nlUpdateBubbles();
    });

    this.maxRange?.addEventListener("input", () => {
      this.nlValidateRanges("max");
      this.nlUpdateBubbles();
    });

    this.minRange?.addEventListener("change", () => {
      this.nlUpdatePriceFilterAndResults();
      this.nlUpdateSummary();
    });

    this.maxRange?.addEventListener("change", () => {
      this.nlUpdatePriceFilterAndResults();
      this.nlUpdateSummary();
    });
  }

  /**
   * @param {string} changedInput
   */
  nlValidateRanges(changedInput) {
    const minVal = Number(this.minRange?.value || 0);
    const maxVal = Number(this.maxRange?.value || 0);

    if (changedInput === "min") {
      if (minVal > maxVal) {
        /** @type {HTMLInputElement|null} */
        const maxRange = this.maxRange;
        if (maxRange) {
          maxRange.value = minVal.toString();
        }
      }
    } else {
      if (maxVal < minVal) {
        /** @type {HTMLInputElement|null} */
        const minRange = this.minRange;
        if (minRange) {
          minRange.value = maxVal.toString();
        }
      }
    }
  }

  /**
   * Update the bubbles
   */
  nlUpdateBubbles() {
    const minVal = Number(this.minRange?.value || 0);
    const maxVal = Number(this.maxRange?.value || 0);

    // Calculate percentage for positioning
    const minPercent = ((minVal - this.min) * 100) / (this.max - this.min);
    const maxPercent = ((maxVal - this.min) * 100) / (this.max - this.min);

    const minBubble = this.minBubble;
    const maxBubble = this.maxBubble;
    if (minBubble && maxBubble) {
      // Update bubble positions and values
      minBubble.innerHTML = this.nlFormatMoney(minVal * 100);
      maxBubble.innerHTML = this.nlFormatMoney(maxVal * 100);

      // Position bubbles
      minBubble.style.left = `calc(${minPercent}% + (${8 - minPercent * 0.15
        }px))`;
      maxBubble.style.left = `calc(${maxPercent}% + (${8 - maxPercent * 0.15
        }px))`;
    }

    // Update slider track appearance
    const slider = this.slider;
    if (slider) {
      slider.style.setProperty("--min-percent", `${minPercent}%`);
      slider.style.setProperty("--max-percent", `${maxPercent}%`);
    }
  }

  /**
   * Updates price filter and results
   */
  nlUpdatePriceFilterAndResults() {
    const facetsForm = this.closest("facets-form-component");
    // @ts-ignore
    if (!facetsForm || typeof facetsForm.updateFilters !== "function") {
      return;
    }

    // @ts-ignore
    facetsForm.updateFilters();
  }

  /**
   * Updates the price summary
   */
  nlUpdateSummary() {
    const minInput = this.minRange;
    const maxInput = this.maxRange;

    const details = this.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");
    if (
      !statusComponent ||
      // @ts-ignore
      typeof statusComponent.updatePriceSummary !== "function"
    ) {
      return;
    }

    // @ts-ignore
    statusComponent.updatePriceSummary(minInput, maxInput);
  }

  nlClearFilter() {
    if (this.minRange) {
      this.minRange.value = this.min.toString();
    }
    if (this.maxRange) {
      this.maxRange.value = this.max.toString();
    }
    this.nlUpdateBubbles();
  }
}

customElements.define(
  "nala-price-slider-facet-component",
  NalaPriceRangeSliderFacetComponent
);

class NalaFacetClearComponent extends NalaBase {
  constructor() {
    super();
    /** @type {HTMLElement|null} */
    this.clearButton = this.querySelector(".clear-filter");
  }

  connectedCallback() {
    this.nlSetupEventListeners();
  }

  disconnectedCallback() {
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.nlHandleFilterUpdate
    );
  }

  nlSetupEventListeners() {
    this.addEventListener("keyup", this.nlHandleKeyUp);
    this.addEventListener("click", this.nlClearFilter);
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.nlHandleFilterUpdate
    );
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  nlClearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest("nala-price-slider-facet-component");
    // @ts-ignore
    if (container && typeof container.clearFilter === "function") {
      // @ts-ignore
      container.clearFilter();
    }

    const details = event.target.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");
    // @ts-ignore
    if (statusComponent && typeof statusComponent.clearSummary === "function") {
      // @ts-ignore
      statusComponent.clearSummary();
    }

    const facetsForm = this.closest("facets-form-component");
    // @ts-ignore
    if (facetsForm && typeof facetsForm.updateFilters === "function") {
      // @ts-ignore
      facetsForm.updateFilters();
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  nlHandleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === "Enter") this.nlClearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  nlHandleFilterUpdate = (event) => {
    if (this.clearButton instanceof Element) {
      this.clearButton.classList.toggle(
        "facets__clear--active",
        event.shouldShowClearAll()
      );
    }
  };
}

customElements.define("nala-facet-clear-component", NalaFacetClearComponent);

class NalaSku extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlSetupEventListeners();
  }

  nlSetupEventListeners() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, (event) => {
      // @ts-ignore
      if (event.detail.data.newProduct) {
        // @ts-ignore
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (
        event.target instanceof HTMLElement &&
        event.target.dataset.productId !== this.dataset.productId
      ) {
        return;
      }

      // @ts-ignore
      const newSku = event.detail.data.html.querySelector("nala-sku");
      if (newSku) {
        this.innerHTML = newSku.innerHTML;
      }
    });
  }
}

customElements.define("nala-sku", NalaSku);

class NalaInventoryStatus extends NalaBase {
  constructor() {
    super();
  }

  connectedCallback() {
    this.nlSetupEventListeners();
  }

  nlSetupEventListeners() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, (event) => {
      // @ts-ignore
      if (event.detail.data.newProduct) {
        // @ts-ignore
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (
        event.target instanceof HTMLElement &&
        event.target.dataset.productId !== this.dataset.productId
      ) {
        return;
      }

      // @ts-ignore
      const newInventoryStatus = event.detail.data.html.querySelector(
        "nala-inventory-status"
      );
      if (newInventoryStatus) {
        this.innerHTML = newInventoryStatus.innerHTML;
      }
    });
  }
}

customElements.define("nala-inventory-status", NalaInventoryStatus);

class NalaBaaTiers extends NalaBase {
  constructor() {
    super();
    /** @type {string|undefined} */
    this.variantId = this.dataset.variantId;
    /** @type {BAATierPrice[]} */
    this.proPrices = this.dataset.proPrices
      ? Object.values(JSON.parse(this.dataset.proPrices) || {})
      : [];
    /** @type {BAATierPrice[]} */
    this.distributorPrices = this.dataset.distributorPrices
      ? Object.values(JSON.parse(this.dataset.distributorPrices) || {})
      : [];
    /** @type {number|undefined} */
    this.basePrice = this.dataset.basePrice
      ? parseFloat(this.dataset.basePrice)
      : 0;
    /** @type {boolean} */
    this.isDistributor = this.dataset.isDistributor
      ? this.dataset.isDistributor === "true"
      : false;
    this.isOpen = false;
  }

  connectedCallback() {
    this.nlUpdateTierPrices();
    this.nlSetupEventListeners();
  }

  nlSetupEventListeners() {
    this.nlOnChangeUpdateTierPrices();
  }

  nlUpdateTierPrices() {
    const prices =
      this.isDistributor && this.distributorPrices.length > 0
        ? this.distributorPrices
        : this.proPrices;
    /** @type {number} */
    let lowestPrice = -1;
    /** @type {BAATierPrice[]} */
    let tierPrices = [];

    // get prices without qty == 1 and get the lowest price
    prices.forEach((price) => {
      const qty = Number(price.price_min_quantity);
      if (qty <= 1) return;
      const priceValue = Number(price.price_unit_price) * 100;
      if (priceValue < Number(lowestPrice) || lowestPrice == -1) {
        lowestPrice = priceValue;
      }
      tierPrices.push({
        price_unit_price: priceValue,
        price_min_quantity: qty,
      });
    });

    // get the html of the tier prices
    const html = tierPrices
      .map((price) => {
        return `
        <div class="nala-baa-tiers__item">
          <div class="nala-baa-tiers__item-title">${price.price_min_quantity
          }+ Units</div>
          <div class="nala-baa-tiers__item-price">${this.nlFormatMoney(
            price.price_unit_price
          )}</div>
        </div>
      `;
      })
      .join("");
    this.isOpen = false;

    // if there are tier prices, show the wrapper
    if (html) {
      this.innerHTML = `
        <div class="nala-baa-tiers__wrapper">
          <div class="nala-baa-tiers__header">
            <div class="nala-baa-tiers__header-icon">
              ${this.nlGetIcon("tag", 59.567)}
            </div>
            <div class="nala-baa-tiers__header-title">
              <span>Buy More - Save More. As low as <strong>${this.nlFormatMoney(
        lowestPrice
      )}</strong></span>
            </div>
            <div class="nala-baa-tiers__header-action">
              ${this.nlGetIcon("chevron-down", 10, 0.1)}
            </div>
          </div>
          <div class="nala-baa-tiers__content nala-hidden">${html}</div>
        </div>`;
      this.nlAddOpenCloseEvent();
    } else {
      this.innerHTML = "";
    }
  }

  nlOnChangeUpdateTierPrices() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, (event) => {
      // @ts-ignore
      if (event.detail.data.newProduct) {
        // @ts-ignore
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (
        event.target instanceof HTMLElement &&
        event.target.dataset.productId !== this.dataset.productId
      ) {
        return;
      }

      const newBaaTiers =
        // @ts-ignore
        event.detail.data.html.querySelector("nala-baa-tiers");
      if (newBaaTiers) {
        this.variantId = newBaaTiers.dataset.variantId;
        this.proPrices = newBaaTiers.dataset.proPrices
          ? Object.values(JSON.parse(newBaaTiers.dataset.proPrices) || {})
          : [];
        this.distributorPrices = newBaaTiers.dataset.distributorPrices
          ? Object.values(
            JSON.parse(newBaaTiers.dataset.distributorPrices) || {}
          )
          : [];
        this.basePrice = newBaaTiers.dataset.basePrice
          ? parseFloat(newBaaTiers.dataset.basePrice)
          : 0;
        this.isDistributor = newBaaTiers.dataset.isDistributor
          ? newBaaTiers.dataset.isDistributor === "true"
          : false;
        this.nlUpdateTierPrices();
      }
    });
  }

  nlAddOpenCloseEvent() {
    this.querySelector(".nala-baa-tiers__header")?.addEventListener(
      "click",
      () => {
        const content = this.querySelector(".nala-baa-tiers__content");
        if (content) {
          content.classList.toggle("nala-hidden", this.isOpen);
        }
        const wrapper = this.querySelector(".nala-baa-tiers__wrapper");
        if (wrapper) {
          wrapper.classList.toggle("is-opened", !this.isOpen);
        }
        this.isOpen = !this.isOpen;
      }
    );
  }
}

customElements.define("nala-baa-tiers", NalaBaaTiers);

class NalaDiscountNext extends NalaBase {
  constructor() {
    super();

    /** @type {string} */
    this.url = this.dataset.url || "/";
  }

  /**
   * Connected callback
   */
  connectedCallback() {
    this.nlSetupEventListeners();
  }

  /**
   * Setup event listeners
   */
  nlSetupEventListeners() {
    this.nlAddOpenModalEvent();
    this.nlCartUpdateEventListener();
  }

  /**
   * Add open modal event
   */
  nlAddOpenModalEvent() {
    const link = this.querySelector(".nala-discount-next__content-link a");
    if (!link) return;

    link.addEventListener("click", (event) => {
      event.preventDefault();
      this.nlOpenModal();
    });
  }

  /**
   * Open modal
   */
  nlOpenModal() {
    const modalContent = this.querySelector(".nala-discount-next__modal");
    if (!modalContent) return;

    // Create or get the dialog element
    let dialog = /** @type {HTMLDialogElement|null} */ (
      document.querySelector("#nala-discount-next-modal-dialog")
    );
    if (!dialog) {
      dialog = document.createElement("dialog");
      dialog.id = "nala-discount-next-modal-dialog";
      dialog.className = "nala-discount-next-modal-dialog dialog-modal";
      dialog.setAttribute("aria-labelledby", "nala-discount-next-modal-title");
      dialog.setAttribute("aria-modal", "true");
      document.body.appendChild(dialog);

      // Set up backdrop click handler (only once)
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) {
          this.nlCloseModal();
        }
      });

      // Set up Escape key handler (only once)
      dialog.addEventListener("keydown", (e) => {
        /** @type {KeyboardEvent} */
        const ke = /** @type {KeyboardEvent} */ (e);
        if (ke.key === "Escape") {
          this.nlCloseModal();
        }
      });
    }

    // Get the modal content HTML
    const contentHtml = modalContent.innerHTML;

    // Set dialog content with title and content
    dialog.innerHTML = `
      <div class="nala-discount-next-modal-content">
        <div class="nala-discount-next-modal-header">
          <h2 id="nala-discount-next-modal-title" class="nala-discount-next-modal-title">All Discounts</h2>
          <button 
            type="button" 
            class="nala-discount-next-modal-close button button-unstyled close-button" 
            aria-label="Close discount modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="nala-discount-next-modal-body">
          ${contentHtml}
        </div>
      </div>
    `;

    // Add close button event listener
    const closeButton = /** @type {HTMLButtonElement|null} */ (
      dialog.querySelector(".nala-discount-next-modal-close")
    );
    if (closeButton) {
      closeButton.addEventListener("click", () => this.nlCloseModal());
    }

    // Show the dialog
    dialog.showModal();
    document.body.classList.add("modal-open");

    // Focus management for accessibility
    if (closeButton) {
      closeButton.focus();
    }
  }

  /**
   * Close modal
   */
  nlCloseModal() {
    const dialog = /** @type {HTMLDialogElement|null} */ (
      document.querySelector("#nala-discount-next-modal-dialog")
    );
    if (dialog) {
      dialog.close();
      document.body.classList.remove("modal-open");
    }
  }

  /**
   * Add cart update event listener
   */
  nlCartUpdateEventListener() {
    document.addEventListener(CartAddEvent.eventName, async () => {
      // get the product discount
      const productDiscount = await this.nlGetProductDiscount(this.url);

      // get the discount content
      const discountContent = this.querySelector(
        ".nala-discount-next__content"
      );
      if (!discountContent) return;

      // update the text
      const text = discountContent.querySelector(
        ".nala-discount-next__content-text"
      );
      if (!text) return;

      if (productDiscount) {
        text.innerHTML = productDiscount;
        discountContent.classList.remove("nala-hidden");
      } else {
        discountContent.classList.add("nala-hidden");
      }
    });
  }

  /**
   * @description get the product data
   * @param {string} url
   * @returns {Promise<string>}
   */
  async nlGetProductDiscount(url) {
    if (url.includes("?")) {
      url = `${url}&view=discount`;
    } else {
      url = `${url}?view=discount`;
    }
    const response = await fetch(url);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const element = doc.getElementById("nalaProductDiscount");
    if (!element) return "";

    // get the nala discount content
    const nalaDiscountContent = element.querySelector(
      ".nala-discount-next__content"
    );
    if (
      !nalaDiscountContent ||
      nalaDiscountContent.classList.contains("nala-hidden")
    )
      return "";

    // get the text content
    const textContent = nalaDiscountContent.querySelector(
      ".nala-discount-next__content-text"
    );
    if (!textContent) return "";
    return textContent.innerHTML;
  }
}

customElements.define("nala-discount-next", NalaDiscountNext);

/**
 * Nala Persistent Cart Component
 * Manages persistent cart functionality for wholesale customers
 * @class
 * @extends {NalaBase}
 */
class NalaPersistentCart extends NalaBase {
  /**
   * Creates an instance of NalaPersistentCart
   * @constructor
   */
  constructor() {
    super();

    /** @type {string} The customer id */
    this.customerId = this.dataset.customerId || "";

    /** @type {string} The hash */
    this.hash = this.dataset.hash || "";
  }

  /**
   * Called when the element is inserted into the DOM
   * Initializes event listeners
   * @returns {void}
   */
  connectedCallback() {
    this.nlInitializePersistentCart();
    this.nlInitializeEventListeners();
  }

  /**
   * Initializes the persistent cart
   * @returns {void}
   */
  nlInitializePersistentCart() {
    const pCartCookie = this.nlGetPersistentCartCookie();
    console.log(pCartCookie, this.hash, this.customerId);
    if (pCartCookie != this.hash && this.customerId) {
      this.nlAddPersistentCart();
    } else if (pCartCookie != this.hash) {
      this.nlRemovePersistentCart();
    } else if (this.customerId) {
      //this.nlGetPersistentCartDiff();
    }
  }
  /**
   * Gets the persistent cart cookie
   * @returns {string}
   */
  nlGetPersistentCartCookie() {
    return this.nlGetCookie('persistent-cart');
  }

  /**
   * Sets the persistent cart cookie
   * @returns {void}
   */
  nlSetPersistentCartCookie() {
    this.nlSetCookie('persistent-cart', this.hash);
  }

  /**
   * Adds the persistent cart
   * @returns {Promise<void>}
   */
  async nlAddPersistentCart() {
    try {
      const response = await fetch(`/apps/nala-persistent-cart/cart/${this.customerId}/${this.hash}`);
      if (!response.ok) {
        throw new Error("Failed to fetch persistent cart");
      }
      this.nlSetPersistentCartCookie();

      /** @type {NalaCart} */
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      if (data.count > 0) {
        const cartItems = data.items.map((item) => {
          return {
            id: item.id,
            quantity: item.quantity,
            properties: Array.isArray(item.properties) ? {} : item.properties,
            selling_plan: item.selling_plan || "",
          };
        });
        await this.nlAddToCart("1", cartItems, false, "");
      }
    } catch (error) {
      console.error("Failed to add persistent cart", error);
    }
  }

  /**
   * Removes the persistent cart
   * @returns {Promise<void>}
   */
  async nlRemovePersistentCart() {
    try {
      await this.nlClearCart();
      this.nlSetPersistentCartCookie();
    } catch (error) {
      console.error("Failed to remove persistent cart", error);
    }
  }

  /**
   * Gets the persistent cart diff
   * @returns {Promise<void>}
   */
  async nlGetPersistentCartDiff() {
    try {
      const cart = await this.nlGetCurrentCart();
      const data = {
        items: cart?.items || [],
        carttoken: cart?.token || "",
      };

      const response = await fetch(`/apps/nala-persistent-cart/diff/${this.customerId}/${this.hash}`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch persistent cart diff");
      }
      const diffData = await response.json();
      if (diffData.enabled === 0) {
        await this.nlClearCart("");
      } else if (diffData.count > 0) {
        await this.nlClearCart("");
        await this.nlAddToCart("1", diffData.items, false, "");
      }
    } catch (error) {
      console.error("Failed to get persistent cart diff", error);
    }
  }

  /**
   * Updates the persistent cart
   * @returns {Promise<void>}
   */
  async nlUpdatePersistentCart() {
    if (!this.customerId || !this.hash) return;

    /** @type {HTMLButtonElement|null} */
    const checkoutBtn = document.querySelector(".cart__checkout-button");
    if (checkoutBtn) {
      checkoutBtn.disabled = true;
    }

    try {
      const cart = await this.nlGetCurrentCart(true);
      const data = {
        items: cart?.items || [],
        carttoken: cart?.token || "",
      };

      const response = await fetch(`/apps/nala-persistent-cart/update/${this.customerId}/${this.hash}`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to update persistent cart");
      }
    }
    catch (error) {
      console.error("Failed to update persistent cart", error);
    } finally {
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
      }
    }
  }

  /**
   * Initializes the event listeners
   * @returns {void}
   */
  nlInitializeEventListeners() {
    document.addEventListener(CartAddEvent.eventName, async () => {
      await this.nlUpdatePersistentCart();
    });
    document.addEventListener("nala-cart-updated", async () => {
      await this.nlUpdatePersistentCart();
    });
  }
}

customElements.define("nala-persistent-cart", NalaPersistentCart);

/**
 * Update on document ready
 */
document.addEventListener("DOMContentLoaded", () => {
  nlAddGalleryImages();
});
